/**
 * Projects Data
 * Contains portfolio projects with descriptions, technologies, and links
 */

import type { Project } from '@/types';

export const projects: Project[] = [
  {
    id: 1,
    title: "LeetCode Company Questions Analytics Dashboard",
    date: "December 2024",
    category: "Web Development",
    status: "Completed",
    description: "An advanced analytics dashboard that analyzes and visualizes company-specific LeetCode questions, providing valuable insights for technical interview preparation. The application fetches data from GitHub repositories, processes LeetCode question patterns, and presents comprehensive analytics to help developers understand which companies ask specific types of coding problems. Features include interactive data visualization, company-specific question filtering, and trend analysis for interview preparation strategies.",
    technologies: ["React", "Next.js", "JavaScript", "TypeScript", "Data Visualization", "GitHub API", "Vercel", "CSS", "Analytics"],
    github: "https://github.com/ashraf8ila/leetcode-company-questions",
    demo: "https://leetcode-company-questions.vercel.app/",
    image: "/react-portfolio/project-previews/leetcode/FullPage.png",
    images: [
      "/react-portfolio/project-previews/leetcode/Companies.png",
      "/react-portfolio/project-previews/leetcode/Filtered.png",
      "/react-portfolio/project-previews/leetcode/Analytics.png",
      "/react-portfolio/project-previews/leetcode/Resources.png",
      "/react-portfolio/project-previews/leetcode/FullPage.png"
    ]
  },
  {
    id: 2,
    title: "Pintos - Operating System Playground",
    date: "April 2024",
    category: "Systems Programming",
    status: "Completed",
    description: "Designed thread scheduling and management features, including priority scheduling and preemption. Implemented system calls, process control, and argument passing for user programs. Enhanced virtual memory capabilities through paging, efficiently managing page fault exceptions for optimized memory performance.",
    technologies: ["C", "Threads", "Synchronization", "Virtualization", "Operating Systems", "System Calls", "Virtual Memory"],
    github: "#",
    demo: "#",
    image: "/project-previews/project-2.svg"
  },
  {
    id: 3,
    title: "Lock Your Secrets Web Application",
    date: "March 2024",
    category: "Cloud Computing",
    status: "Completed",
    description: "Developed a web application for securely storing sensitive data using SHA-256 hashing. Designed an interconnected cloud infrastructure, where AWS Amplify hosts the frontend, API Gateway manages API requests, Lambda functions oversee processing, DynamoDB stores hashed data, S3 stores encrypted objects, and IAM enforces secure access control.",
    technologies: ["AWS Amplify", "Lambda", "API Gateway", "DynamoDB", "IAM", "S3", "SHA-256", "Cloud Computing"],
    github: "#",
    demo: "#",
    image: "/project-previews/project-3.png"
  },
  {
    id: 4,
    title: "Multithreaded Relational Database Management System",
    date: "September 2024",
    category: "Database Systems",
    status: "Completed",
    description: "Developed a multithreaded relational DBMS using PostgreSQL, incorporating file management, buffer management, schema design, data organization, B+ tree indexing, and query optimization, leading to a 32% performance boost and a 27% reduction in retrieval time.",
    technologies: ["Java", "SQL", "PostgreSQL", "Relational Database", "Query Optimization", "B+ Tree", "Multithreading"],
    github: "#",
    demo: "#",
    image: "/project-previews/project-4.png"
  },
  {
    id: 5,
    title: "EMNIST",
    date: "Fall 2023",
    category: "Machine Learning",
    status: "Completed",
    description: "Applied various CNN to classify EMNIST dataset and achieved accuracy around 95%.",
    technologies: ["numpy", "pandas", "matplotlib", "sns", "scikit", "streamlit", "Pytorch", "jupyter", "Image Classification"],
    github: "#",
    demo: "#",
    image: "/project-previews/project-5.png"
  },
  {
    id: 6,
    title: "WordCount",
    date: "Fall 2023",
    category: "Big Data",
    status: "Completed",
    description: "Applied the concept of map-reduce to parallely do a wordcount on a large text dataset.",
    technologies: ["Hadoop", "Pyspark", "Map-reduce"],
    github: "#",
    demo: "#",
    image: "/project-previews/project-6.png"
  },
  {
    id: 7,
    title: "SeedLab",
    date: "Fall 2023",
    category: "Cybersecurity",
    status: "Completed",
    description: "Successfully completed security related challenges offered by Seedlabs under a debian based virtual environment.",
    technologies: ["C", "Python", "Cybersecurity", "Network Security", "Web Security", "Cryptography", "Wireshark", "Ubuntu"],
    github: "#",
    demo: "#",
    image: "/project-previews/project-7.png"
  },
  {
    id: 8,
    title: "ChestMNIST",
    date: "Spring 2024",
    category: "Deep Learning",
    status: "Completed",
    description: "Used the Chestmnist dataset and applied ResNet, DenseNet, GoogleNet, VGG16, ResNeXt, ResNet50 and compared the results to derive the most suitable NN to work with Chest X-Rays and eventually prevent Chest related diseases.",
    technologies: ["Resnet18", "DenseNet121", "GoogleNet", "VGG16", "ResNeXt", "ResNet50"],
    github: "#",
    demo: "#",
    image: "/project-previews/project-8.png"
  },
  {
    id: 9,
    title: "Facebook Sentiment Analysis",
    date: "Spring 2018",
    category: "NLP",
    status: "Completed",
    description: "Used Facebook Graph API to understand the sentiment involved in the comments section of a post",
    technologies: ["Graph API", "NLP"],
    github: "#",
    demo: "#",
    image: "/project-previews/project-9.png"
  },
  {
    id: 10,
    title: "Stock Market Prediction",
    date: "Spring 2018",
    category: "Data Science",
    status: "Completed",
    description: "Applied Linear Regression to the yahoo finance dataset to predict the market trends.",
    technologies: ["Linear Regression", "finpy"],
    github: "#",
    demo: "#",
    image: "/project-previews/project-10.png"
  },
  {
    id: 11,
    title: "Quadcopter with BT Remote",
    date: "Fall 2018",
    category: "IoT",
    status: "Completed",
    description: "An IoT project involving Arduino, Bluetooth module and a Web Interface made with Php to control a Quad-copter built from scratch. Our goal was to test its limitations in the field of logistics.",
    technologies: ["Embedded C"],
    github: "#",
    demo: "#",
    image: "/project-previews/project-11.png"
  },
  {
    id: 12,
    title: "Handwriting Recognition",
    date: "Spring 2017",
    category: "Computer Vision",
    status: "Completed",
    description: "A project to understand the working of a simple Neural Net, coded in MATLAB to understand human handwriting.",
    technologies: ["MATLAB (Image Processing Toolbox)", "CNN"],
    github: "#",
    demo: "#",
    image: "/project-previews/project-12.png"
  },
  {
    id: 13,
    title: "College Login Portal",
    date: "Fall 2017",
    category: "Web Development",
    status: "Completed",
    description: "A beginner level project to understand the working of frontend and backend of a system and to learn how to design a efficient schema.",
    technologies: ["HTML", "CSS", "PHP", "SQL", "JavaScript (DBMS)"],
    github: "#",
    demo: "#",
    image: "/project-previews/project-8.svg"
  }
];

